import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getStandardsByType } from '../../../requests/learning';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import { findCategories, findSubcategories } from '../Standards.utils';
import Categories from './Categories';
import SubCategories from './SubCategories';
import StandardsList from './StandardsList';
import StandardTypeSelector from './StandardTypeSelector';
import { showErrorMessage } from '../../../helpers/showMessage';
import { isUnauthorized } from '../../../utils/common';
import { AppHeader } from '../../../app/common/sharedComponents/AppHeader';
import { useTranslation } from 'react-i18next';
import { DropdownSingle } from '../../../app/common/sharedComponents/DropdownSingle';
import { IDropdownOption } from '../../../app/@types/dropdown';

const Wrapper = styled.div``;

const Cards = styled.div`
  margin-bottom: 87px;
  padding: 25px 20px;
  margin-top: 80px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 13px;
`;

const EmptyList = styled.div`
  padding: 20px 24px;
  font-size: 22px;
  line-height: 28px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 2px;
  color: #031c38;
`;

const Headers = styled.div`
  display: flex;
  column-gap: 13px;
  margin-bottom: 13px;
  justify-content: space-between;

  .header-tag {
    width: 50%;
    font-size: 12px;
    .control-select-filter-inner {
      height: 43px;
      box-sizing: border-box;
    }
  }
`;

const performanceOptions = [
  {
    label: 'All',
    value: 'all',
  },
  {
    label: 'Service',
    value: 'service',
  },
  {
    label: 'Efficiency',
    value: 'efficiency',
  },
  {
    label: 'EI Behavioral',
    value: 'employeeBehavioural',
  },
  {
    label: 'Cleanliness',
    value: 'cleanliness',
  },
  {
    label: 'Product',
    value: 'product',
  },
  {
    label: 'Sales Opportunity',
    value: 'salesOpportunity',
  },
  {
    label: 'Food Quality',
    value: 'food',
  },
];

const Standards = () => {
  const { t } = useTranslation();
  const [standardsData, setStandardsData] = useState<any>(undefined);
  const [standardsCategories, setStandardsCategories] =
    useState<any>(undefined);
  const [standardsSubCategories, setStandardsSubCategories] =
    useState<any>(undefined);
  const [subcategorySelected, setSubcategorySelected] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const newQueryParameters: URLSearchParams = new URLSearchParams();

  const [standardType, setStandardType] = useState<IDropdownOption | null>(
    null
  );
  const [performanceOpt, setPerformanceOpt] = useState(performanceOptions[0]);

  useEffect(() => {
    if (!standardType) {
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const standards = await getStandardsByType(standardType.value);
        if (standards && standards.length > 0) {
          setStandardsData(standards);
          const categories = findCategories(standards).filter(
            (category) => {
              const subCats = findSubcategories(standards, category.id);
              return subCats && subCats.length > 0;
            }
          );
          setStandardsCategories(categories);
          const defaultCategory = categories[0];

          const subcategories = findSubcategories(
            standards,
            defaultCategory.id
          );
          setStandardsSubCategories(subcategories);
          setSubcategorySelected(subcategories[0]);
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
        showErrorMessage('Unable to get list', !isUnauthorized(err));
      }
    })();
  }, [standardType]);

  const onSubcategoryClickHandler = (subcategoryId) => {
    const subCategory = standardsSubCategories.find(
      (subCat) => subCat.id === subcategoryId
    );
    setSubcategorySelected(subCategory);
  };

  const onCategoryClickHandler = (categoryId) => {
    const subcategories = findSubcategories(standardsData, categoryId);
    setStandardsSubCategories(subcategories);
    setSubcategorySelected(subcategories[0]);
  };

  const onStandardTypeChangeHandler = (option) => {
    newQueryParameters.set('type', option.value as string);
    setStandardType(option);
  };

  return (
    <Wrapper>
      <div className="header">
        <AppHeader
          title={t('COMMON.LEARNING.TITLES.LUXURY_STANDARDS')}
          hasBackButton={true}
        />
      </div>
      <Cards>
        <Content>
          <Headers>
            <StandardTypeSelector
              standardType={standardType}
              onChangeHandler={(standard) => {
                onStandardTypeChangeHandler(standard);
              }}
            />
            <div className="header-tag">
              <DropdownSingle
                options={performanceOptions}
                defaultOption={performanceOpt}
                callback={(option) => {
                  setPerformanceOpt(option);
                }}
                isClearable={false}
              />
            </div>
          </Headers>
          {loading ? (
            <LoadingContent layout="flex" />
          ) : (
            <>
              {!subcategorySelected ||
              typeof subcategorySelected === 'undefined' ? (
                <EmptyList>
                  <span>Great content is</span>
                  <span>coming soon!</span>
                </EmptyList>
              ) : (
                <>
                  <Categories
                    data={standardsCategories}
                    onClickHandler={(id) => onCategoryClickHandler(id)}
                  />
                  <SubCategories
                    data={standardsSubCategories}
                    onSubcategoryClickHandler={(id) => {
                      onSubcategoryClickHandler(id);
                    }}
                  />
                  <StandardsList
                    data={subcategorySelected}
                    performaceOpt={performanceOpt.value}
                  />
                </>
              )}
            </>
          )}
        </Content>
      </Cards>
    </Wrapper>
  );
};

export default Standards;
